import { useEffect, useState } from "react"
import ReactQuill from "react-quill"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { userService } from "services/user/user.service"

import "react-quill/dist/quill.snow.css"

type ModalSendMessageProps = {
  open: boolean
  onClose: () => void
  userId: string
  recipientEmail?: string
}

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"]
  ]
}

const quillFormats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "link"]

const isMessageEmpty = (html: string): boolean => {
  // Strip tags and check for any non-whitespace content
  const text = html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim()
  return text.length === 0
}

export const ModalSendMessage = ({ open, onClose, userId, recipientEmail }: ModalSendMessageProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setSubject("")
      setMessage("")
      setIsSubmitting(false)
    }
  }, [open])

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error(t("user.sendMessage.subjectRequired") as string)
      return
    }
    if (isMessageEmpty(message)) {
      toast.error(t("user.sendMessage.messageRequired") as string)
      return
    }

    setIsSubmitting(true)
    const success = await userService.sendUserMessage(userId, { subject: subject.trim(), message })
    setIsSubmitting(false)

    if (success) {
      toast.success(t("user.sendMessage.success") as string)
      onClose()
    }
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className="relative flex flex-col modal-box w-[600px] max-w-[95vw]">
        <h3 className="text-lg font-semibold mb-2">{t("user.sendMessage.title")}</h3>
        {recipientEmail && (
          <p className="text-sm opacity-70 mb-4 break-all">
            {t("user.sendMessage.to")} <span className="font-medium">{recipientEmail}</span>
          </p>
        )}

        <div className="flex flex-col gap-4">
          <div>
            <label className="label">
              <span className="label-text">{t("user.sendMessage.subject")}</span>
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("user.sendMessage.subjectPlaceholder") as string}
              maxLength={200}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="label">
              <span className="label-text">{t("user.sendMessage.message")}</span>
            </label>
            <div className="bg-white text-black rounded">
              <ReactQuill
                theme="snow"
                value={message}
                onChange={setMessage}
                modules={quillModules}
                formats={quillFormats}
                readOnly={isSubmitting}
              />
            </div>
          </div>
        </div>

        <div className="modal-action flex items-center space-x-4 mt-6">
          <button className="btn btn-secondary flex-1" onClick={onClose} disabled={isSubmitting} type="button">
            {t("user.sendMessage.cancel")}
          </button>
          <button
            className={clsx("btn btn-primary flex-1", { loading: isSubmitting })}
            onClick={handleSend}
            disabled={isSubmitting}
            type="button"
          >
            {t("user.sendMessage.send")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
